import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UnidadeInput } from "@/components/UnidadeInput";
import { PaisInput } from "@/components/PaisInput";
import { InstituicaoInput } from "@/components/InstituicaoInput";
import { GrauInput } from "@/components/GrauInput";
import { useUnsavedGuard } from "@/hooks/useUnsavedGuard";
import { toast } from "sonner";

const empty = (turmaId) => ({ nome: "", nome_guerra: "", rg: "", matricula: "", identificacao_funcional: "", turma_id: turmaId || "", grau_nome: "", instituicao_nome: "", unidade_nome: "", pais_nome: "Brasil", observacoes: "" });

export function AlunoFormDialog({ open, onOpenChange, aluno, onSaved, lockTurmaId }) {
  const [turmas, setTurmas] = useState([]);
  const [listas, setListas] = useState({ grau: [], instituicao: [], unidade: [], pais: [] });
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const { setBaseline, guard } = useUnsavedGuard();

  useEffect(() => {
    if (!open) return;
    api.get("/turmas").then((r) => setTurmas(r.data));
    ["grau", "instituicao", "unidade", "pais"].forEach((tipo) =>
      api.get(`/listas/${tipo}`).then((r) => setListas((s) => ({ ...s, [tipo]: r.data }))));
    const f = aluno
      ? { nome: aluno.nome || "", nome_guerra: aluno.nome_guerra || "", rg: aluno.rg || "", matricula: aluno.matricula || "", identificacao_funcional: aluno.identificacao_funcional || "", turma_id: aluno.turma_id || "", grau_nome: aluno.grau_nome || "", instituicao_nome: aluno.instituicao_nome || "", unidade_nome: aluno.unidade_nome || "", pais_nome: aluno.pais_nome || "", observacoes: aluno.observacoes || "" }
      : empty(lockTurmaId);
    setForm(f); setBaseline(f);
  }, [open, aluno, lockTurmaId]);

  const save = async () => {
    if (saving) return;  // ignora cliques repetidos até a resposta do backend
    setSaving(true);
    try {
      const uni = (form.unidade_nome || "").trim();
      const match = listas.unidade.find((u) => (u.nome || "").toLowerCase() === uni.toLowerCase());
      const paisNome = (form.pais_nome || "").trim();
      const paisMatch = listas.pais.find((p) => (p.nome || "").toLowerCase() === paisNome.toLowerCase());
      const instNome = (form.instituicao_nome || "").trim();
      const instMatch = listas.instituicao.find((it) => (it.nome || "").toLowerCase() === instNome.toLowerCase());
      const grauNome = (form.grau_nome || "").trim();
      const grauMatch = listas.grau.find((g) => (g.nome || "").toLowerCase() === grauNome.toLowerCase());
      const payload = {
        ...form,
        grau_nome: grauNome || null,
        grau_id: grauMatch ? grauMatch.id : null,
        instituicao_nome: instNome || null,
        instituicao_id: instMatch ? instMatch.id : "",
        unidade_nome: uni || null,
        unidade_id: match ? match.id : null,
        pais_nome: paisNome || null,
        pais_id: paisMatch ? paisMatch.id : null,
      };
      if (aluno) { await api.put(`/alunos/${aluno.id}`, payload); toast.success("Aluno atualizado"); }
      else { await api.post("/alunos", payload); toast.success("Aluno cadastrado"); }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={guard(onOpenChange, form)}>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle>{aluno ? "Editar aluno" : "Novo aluno"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input data-testid="aluno-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
          <div><Label>Nome de guerra</Label><Input data-testid="aluno-nome-guerra" value={form.nome_guerra} onChange={(e) => setForm({ ...form, nome_guerra: e.target.value })} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>RG</Label><Input data-testid="aluno-rg" value={form.rg} onChange={(e) => setForm({ ...form, rg: e.target.value })} /></div>
            <div><Label>Matrícula</Label><Input data-testid="aluno-matricula" value={form.matricula} onChange={(e) => setForm({ ...form, matricula: e.target.value })} /></div>
            <div><Label>Id. funcional</Label><Input data-testid="aluno-idfuncional" value={form.identificacao_funcional} onChange={(e) => setForm({ ...form, identificacao_funcional: e.target.value })} /></div>
          </div>
          <div>
            <Label>Turma</Label>
            <Select value={form.turma_id} onValueChange={(v) => setForm({ ...form, turma_id: v })} disabled={!!lockTurmaId}>
              <SelectTrigger data-testid="aluno-turma"><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {turmas.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome} — {t.curso_nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Grau hierárquico</Label>
              <GrauInput
                value={form.grau_nome}
                onChange={(v) => setForm({ ...form, grau_nome: v })}
                opcoes={listas.grau}
                testid="aluno-grau"
                label={null}
              />
            </div>
            <div>
              <Label>Instituição</Label>
              <InstituicaoInput
                value={form.instituicao_nome}
                onChange={(v) => setForm({ ...form, instituicao_nome: v })}
                opcoes={listas.instituicao}
                testid="aluno-instituicao"
                label={null}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <UnidadeInput
              value={form.unidade_nome}
              onChange={(v) => setForm({ ...form, unidade_nome: v })}
              opcoes={listas.unidade}
              testid="aluno-unidade"
            />
            <PaisInput
              value={form.pais_nome}
              onChange={(v) => setForm({ ...form, pais_nome: v })}
              opcoes={listas.pais}
              testid="aluno-pais"
            />
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea data-testid="aluno-observacoes" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </div>
        </div>
        <DialogFooter><Button data-testid="salvar-aluno-btn" onClick={save} disabled={saving || !form.nome || !form.turma_id}>{saving ? "Salvando…" : (aluno ? "Salvar alterações" : "Salvar")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
